import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Smartphone, X, Share, Plus, MoreVertical } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

export default function AddToHomeScreen({ variant = 'button' }) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Listen for beforeinstallprompt event (Chrome/Edge on Android)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show native install prompt (Android Chrome)
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      // Show manual instructions
      setShowPrompt(true);
    }
  };

  // Don't show if already installed
  if (isStandalone) {
    return null;
  }

  // For 'full' variant on Profile page - always show instructions
  if (variant === 'full') {
    return (
      <div className="space-y-4">
        {/* iPhone Instructions */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="font-medium text-sm mb-3 flex items-center gap-2">
            📱 iPhone / iPad (Safari)
          </p>
          <ol className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">1</span>
              <span>Ouvrez cette page dans <strong>Safari</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">2</span>
              <span>Appuyez sur <strong>Partager</strong> <Share className="inline h-4 w-4" /></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">3</span>
              <span>Sélectionnez <strong>"Sur l'écran d'accueil"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">4</span>
              <span>Appuyez sur <strong>"Ajouter"</strong></span>
            </li>
          </ol>
        </div>

        {/* Android Instructions */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <p className="font-medium text-sm mb-3 flex items-center gap-2">
            🤖 Android (Chrome)
          </p>
          <ol className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">1</span>
              <span>Ouvrez cette page dans <strong>Chrome</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">2</span>
              <span>Appuyez sur le menu <strong>⋮</strong> (3 points)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">3</span>
              <span>Sélectionnez <strong>"Ajouter à l'écran d'accueil"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shrink-0">4</span>
              <span>Confirmez avec <strong>"Ajouter"</strong></span>
            </li>
          </ol>
        </div>
      </div>
    );
  }

  // Button variant - Only show on mobile
  if (!isIOS && !isAndroid) {
    return null;
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleInstallClick}
        className="flex items-center gap-2"
        data-testid="add-to-home-btn"
      >
        <Smartphone className="h-4 w-4" />
        Installer l'app
      </Button>

      <Dialog open={showPrompt} onOpenChange={setShowPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Installer Vitalis Sports
            </DialogTitle>
            <DialogDescription>
              Ajoutez l'application à votre écran d'accueil pour un accès rapide
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {isIOS && (
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="font-medium text-sm mb-3 flex items-center gap-2">
                    📱 Sur iPhone / iPad (Safari)
                  </p>
                  <ol className="space-y-3 text-sm">
                    <li className="flex items-start gap-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                      <div>
                        <p>Appuyez sur le bouton <strong>Partager</strong></p>
                        <div className="mt-1 inline-flex items-center justify-center w-8 h-8 bg-blue-500 rounded">
                          <Share className="h-4 w-4 text-white" />
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                      <div>
                        <p>Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong></p>
                        <div className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                          <Plus className="h-4 w-4" />
                          Sur l'écran d'accueil
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                      <p>Appuyez sur <strong>"Ajouter"</strong> en haut à droite</p>
                    </li>
                  </ol>
                </div>
                
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    ⚠️ Cette procédure fonctionne uniquement avec <strong>Safari</strong>. 
                    Si vous utilisez Chrome ou un autre navigateur, ouvrez d'abord cette page dans Safari.
                  </p>
                </div>
              </div>
            )}

            {isAndroid && (
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="font-medium text-sm mb-3 flex items-center gap-2">
                    🤖 Sur Android (Chrome)
                  </p>
                  <ol className="space-y-3 text-sm">
                    <li className="flex items-start gap-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                      <div>
                        <p>Appuyez sur le menu <strong>⋮</strong> (3 points)</p>
                        <div className="mt-1 inline-flex items-center justify-center w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded">
                          <MoreVertical className="h-4 w-4" />
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                      <div>
                        <p>Appuyez sur <strong>"Ajouter à l'écran d'accueil"</strong></p>
                        <div className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                          <Plus className="h-4 w-4" />
                          Ajouter à l'écran d'accueil
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                      <p>Confirmez en appuyant sur <strong>"Ajouter"</strong></p>
                    </li>
                  </ol>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-400">
                    💡 Sur certains appareils, une bannière "Ajouter Vitalis Sports à l'écran d'accueil" 
                    peut apparaître automatiquement en bas de l'écran.
                  </p>
                </div>
              </div>
            )}

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">✨ Avantages de l'application :</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Accès rapide depuis l'écran d'accueil</li>
                <li>• Interface plein écran (sans barre de navigateur)</li>
                <li>• Icône dédiée comme une vraie app</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowPrompt(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
